import React, { useRef, useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  useTheme,
  alpha,
  Slider,
  Grid,
  Tooltip,
  Zoom
} from '@mui/material';
import { HeatMapGrid } from 'react-grid-heatmap';

const MultiDeliveryPitchMap = ({ deliveries, height = 400 }) => {
  const theme = useTheme();
  const canvasRef = useRef(null);
  const [selectedHeatmap, setSelectedHeatmap] = useState('intensity');
  const [pitchDimensions, setPitchDimensions] = useState({ width: 20.12, height: 3.05 });
  const [impactData, setImpactData] = useState([]);
  const [heatmapData, setHeatmapData] = useState([]);

  useEffect(() => {
    if (deliveries.length === 0) return;

    // Extract pitch impact points
    const points = deliveries.map(d => ({
      x: d.pitch_impact.x,
      y: d.pitch_impact.y,
      speed: d.metrics.speed_kmh,
      swingType: d.metrics.swing_type,
      deliveryType: d.delivery_type,
      bounceHeight: d.pitch_impact.bounce_height,
      lengthCategory: d.pitch_impact.length_category,
      lineCategory: d.pitch_impact.line_category
    }));

    setImpactData(points);

    // Create grid for heatmap (20x20 grid)
    const gridSize = 20;
    const grid = Array(gridSize).fill().map(() => Array(gridSize).fill(0));
    
    points.forEach(point => {
      // Convert to grid coordinates
      const gridX = Math.floor((point.x / pitchDimensions.width) * gridSize);
      const gridY = Math.floor(((point.y + pitchDimensions.height / 2) / pitchDimensions.height) * gridSize);
      
      if (gridX >= 0 && gridX < gridSize && gridY >= 0 && gridY < gridSize) {
        if (selectedHeatmap === 'intensity') {
          grid[gridY][gridX] += 1; // Count of deliveries
        } else if (selectedHeatmap === 'speed') {
          grid[gridY][gridX] = Math.max(grid[gridY][gridX], point.speed); // Max speed
        } else if (selectedHeatmap === 'bounce') {
          grid[gridY][gridX] = Math.max(grid[gridY][gridX], point.bounceHeight); // Max bounce height
        }
      }
    });

    // Normalize for heatmap display
    const normalizedGrid = grid.map(row => 
      row.map(value => {
        if (selectedHeatmap === 'intensity') {
          return value / deliveries.length; // Normalize to 0-1
        } else if (selectedHeatmap === 'speed') {
          return value / 160; // Normalize to max 160 km/h
        } else if (selectedHeatmap === 'bounce') {
          return value / 2; // Normalize to max 2m bounce
        }
        return value;
      })
    );

    setHeatmapData(normalizedGrid);

    // Draw pitch map with heatmap overlay
    drawPitchMap(points, normalizedGrid);
  }, [deliveries, selectedHeatmap, pitchDimensions]);

  const drawPitchMap = (points, heatmapGrid) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    const width = canvas.width;
    const height = canvas.height;
    
    // Clear canvas
    ctx.clearRect(0, 0, width, height);
    
    // Draw pitch background (green)
    ctx.fillStyle = alpha(theme.palette.success.main, 0.1);
    ctx.fillRect(0, 0, width, height);
    
    // Draw pitch outline
    ctx.strokeStyle = alpha(theme.palette.success.main, 0.3);
    ctx.lineWidth = 2;
    ctx.strokeRect(0, 0, width, height);
    
    // Draw crease lines
    ctx.strokeStyle = alpha(theme.palette.warning.main, 0.4);
    ctx.lineWidth = 1;
    ctx.setLineDash([5, 5]);
    
    // Bowling crease (20.12m from batting crease)
    const bowlingCreaseX = (20.12 / pitchDimensions.width) * width;
    ctx.beginPath();
    ctx.moveTo(bowlingCreaseX, 0);
    ctx.lineTo(bowlingCreaseX, height);
    ctx.stroke();
    
    // Middle stumps (center line)
    ctx.strokeStyle = alpha(theme.palette.info.main, 0.4);
    ctx.beginPath();
    ctx.moveTo(0, height / 2);
    ctx.lineTo(width, height / 2);
    ctx.stroke();
    
    ctx.setLineDash([]);
    
    // Draw heatmap overlay
    const cellWidth = width / heatmapGrid[0].length;
    const cellHeight = height / heatmapGrid.length;
    
    heatmapGrid.forEach((row, y) => {
      row.forEach((value, x) => {
        if (value > 0) {
          // Color intensity based on heatmap type
          let color;
          if (selectedHeatmap === 'intensity') {
            const intensity = Math.min(value * 2, 1);
            color = `rgba(255, ${255 - intensity * 200}, 0, ${0.3 + intensity * 0.4})`;
          } else if (selectedHeatmap === 'speed') {
            const speedIntensity = Math.min(value * 2, 1);
            color = `rgba(255, ${100 - speedIntensity * 100}, 0, ${0.3 + speedIntensity * 0.4})`;
          } else {
            const bounceIntensity = Math.min(value * 2, 1);
            color = `rgba(0, ${100 + bounceIntensity * 155}, 255, ${0.3 + bounceIntensity * 0.4})`;
          }
          
          ctx.fillStyle = color;
          ctx.fillRect(x * cellWidth, y * cellHeight, cellWidth, cellHeight);
        }
      });
    });
    
    // Draw individual impact points
    points.forEach((point, index) => {
      const x = (point.x / pitchDimensions.width) * width;
      const y = ((point.y + pitchDimensions.height / 2) / pitchDimensions.height) * height;
      
      // Determine point color based on swing type
      let pointColor;
      switch(point.swingType) {
        case 'inswing':
          pointColor = theme.palette.info.main;
          break;
        case 'outswing':
          pointColor = theme.palette.success.main;
          break;
        case 'straight':
          pointColor = theme.palette.warning.main;
          break;
        default:
          pointColor = theme.palette.text.secondary;
      }
      
      // Draw point with glow effect
      ctx.beginPath();
      ctx.arc(x, y, 6, 0, Math.PI * 2);
      ctx.fillStyle = alpha(pointColor, 0.8);
      ctx.fill();
      
      // Outer ring for speed indication
      ctx.beginPath();
      ctx.arc(x, y, 8 + (point.speed / 160) * 10, 0, Math.PI * 2);
      ctx.strokeStyle = alpha(pointColor, 0.4);
      ctx.lineWidth = 2;
      ctx.stroke();
      
      // Delivery number
      ctx.fillStyle = theme.palette.background.paper;
      ctx.font = 'bold 10px Arial';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText((index + 1).toString(), x, y);
    });
    
    // Draw legend
    drawLegend(ctx, width, height);
  };

  const drawLegend = (ctx, width, height) => {
    const legendY = 20;
    const legendX = width - 150;
    
    // Legend background
    ctx.fillStyle = alpha(theme.palette.background.paper, 0.9);
    ctx.fillRect(legendX - 10, legendY - 10, 140, 110);
    ctx.strokeStyle = alpha(theme.palette.divider, 0.5);
    ctx.lineWidth = 1;
    ctx.strokeRect(legendX - 10, legendY - 10, 140, 110);
    
    // Legend title
    ctx.fillStyle = theme.palette.text.primary;
    ctx.font = 'bold 12px Arial';
    ctx.textAlign = 'left';
    ctx.fillText('Impact Points Legend', legendX, legendY);
    
    // Inswing
    ctx.beginPath();
    ctx.arc(legendX + 10, legendY + 25, 5, 0, Math.PI * 2);
    ctx.fillStyle = theme.palette.info.main;
    ctx.fill();
    ctx.fillStyle = theme.palette.text.secondary;
    ctx.font = '10px Arial';
    ctx.fillText('Inswing', legendX + 25, legendY + 28);
    
    // Outswing
    ctx.beginPath();
    ctx.arc(legendX + 10, legendY + 45, 5, 0, Math.PI * 2);
    ctx.fillStyle = theme.palette.success.main;
    ctx.fill();
    ctx.fillStyle = theme.palette.text.secondary;
    ctx.fillText('Outswing', legendX + 25, legendY + 48);
    
    // Straight
    ctx.beginPath();
    ctx.arc(legendX + 10, legendY + 65, 5, 0, Math.PI * 2);
    ctx.fillStyle = theme.palette.warning.main;
    ctx.fill();
    ctx.fillStyle = theme.palette.text.secondary;
    ctx.fillText('Straight', legendX + 25, legendY + 68);
    
    // Ring indicates speed
    ctx.beginPath();
    ctx.arc(legendX + 10, legendY + 85, 8, 0, Math.PI * 2);
    ctx.strokeStyle = alpha(theme.palette.text.secondary, 0.5);
    ctx.lineWidth = 2;
    ctx.stroke();
    ctx.fillStyle = theme.palette.text.secondary;
    ctx.fillText('Ring = Speed', legendX + 25, legendY + 88);
  };

  const getHeatmapTitle = () => {
    switch(selectedHeatmap) {
      case 'intensity': return 'Delivery Intensity Heatmap';
      case 'speed': return 'Speed Heatmap (km/h)';
      case 'bounce': return 'Bounce Height Heatmap (m)';
      default: return 'Pitch Impact Heatmap';
    }
  };

  const getHeatmapDescription = () => {
    switch(selectedHeatmap) {
      case 'intensity': return 'Areas with more deliveries are shown in red';
      case 'speed': return 'Faster deliveries shown in brighter colors';
      case 'bounce': return 'Higher bounce points shown in blue';
      default: return '';
    }
  };

  // Calculate statistics
  const stats = {
    totalDeliveries: deliveries.length,
    avgImpactX: impactData.reduce((sum, p) => sum + p.x, 0) / impactData.length,
    avgImpactY: impactData.reduce((sum, p) => sum + p.y, 0) / impactData.length,
    lengthDistribution: {},
    lineDistribution: {}
  };

  impactData.forEach(p => {
    stats.lengthDistribution[p.lengthCategory] = (stats.lengthDistribution[p.lengthCategory] || 0) + 1;
    stats.lineDistribution[p.lineCategory] = (stats.lineDistribution[p.lineCategory] || 0) + 1;
  });

  return (
    <Paper sx={{ p: 3 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
          {getHeatmapTitle()}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {getHeatmapDescription()}
        </Typography>
      </Box>

      {/* Heatmap type selector */}
      <Box sx={{ mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <Typography variant="body2" gutterBottom>
              Heatmap Type:
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {['intensity', 'speed', 'bounce'].map((type) => (
                <Tooltip 
                  key={type} 
                  title={type === 'intensity' ? 'Delivery frequency' : 
                         type === 'speed' ? 'Ball speed' : 'Bounce height'}
                  TransitionComponent={Zoom}
                >
                  <Box
                    component="button"
                    onClick={() => setSelectedHeatmap(type)}
                    sx={{
                      px: 2,
                      py: 1,
                      border: `1px solid ${
                        selectedHeatmap === type 
                          ? theme.palette.primary.main 
                          : alpha(theme.palette.divider, 0.5)
                      }`,
                      borderRadius: 1,
                      backgroundColor: selectedHeatmap === type 
                        ? alpha(theme.palette.primary.main, 0.1) 
                        : 'transparent',
                      color: selectedHeatmap === type 
                        ? theme.palette.primary.main 
                        : theme.palette.text.secondary,
                      cursor: 'pointer',
                      fontSize: '0.875rem',
                      textTransform: 'capitalize',
                      '&:hover': {
                        backgroundColor: alpha(theme.palette.primary.main, 0.05),
                      }
                    }}
                  >
                    {type}
                  </Box>
                </Tooltip>
              ))}
            </Box>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Typography variant="body2" gutterBottom>
              Pitch Scale:
            </Typography>
            <Slider
              value={pitchDimensions.width}
              onChange={(e, newValue) => setPitchDimensions({...pitchDimensions, width: newValue})}
              min={15}
              max={25}
              step={0.1}
              valueLabelDisplay="auto"
              valueLabelFormat={(value) => `${value}m`}
              sx={{ maxWidth: 200 }}
            />
          </Grid>
        </Grid>
      </Box>

      {/* Pitch Map Canvas */}
      <Box sx={{ 
        position: 'relative', 
        height: height, 
        mb: 3,
        border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
        borderRadius: 1,
        overflow: 'hidden'
      }}>
        <canvas
          ref={canvasRef}
          width={800}
          height={600}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'contain'
          }}
        />
      </Box>

      {/* Statistics */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle2" gutterBottom color="text.secondary">
              Impact Statistics
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="caption" display="block" color="text.secondary">
                  Avg Distance
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {stats.avgImpactX ? stats.avgImpactX.toFixed(2) : '0.00'}m
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" display="block" color="text.secondary">
                  Avg Lateral
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {stats.avgImpactY ? stats.avgImpactY.toFixed(2) : '0.00'}m
                </Typography>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
        
        <Grid item xs={12} md={6}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle2" gutterBottom color="text.secondary">
              Most Common Area
            </Typography>
            {Object.entries(stats.lengthDistribution)
              .sort((a, b) => b[1] - a[1])
              .slice(0, 1)
              .map(([category, count]) => (
                <Typography key={category} variant="body1" sx={{ fontWeight: 500 }}>
                  {category.replace('_', ' ')} ({count}/{stats.totalDeliveries})
                </Typography>
              ))}
          </Paper>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default MultiDeliveryPitchMap;